import { useState } from "react";
import { ChevronDown, ChevronRight, Copy, Check } from "lucide-react";
import { clsx } from "clsx";

/**
 * @typedef {Object} JsonPreviewProps
 * @property {Object} content - The content object to display
 * @property {string} title - Optional title for the preview
 * @property {boolean} defaultExpanded - Whether to show expanded by default
 */

/**
 * @param {JsonPreviewProps} props
 */
export default function JsonPreview({
  content,
  title = "JSON Preview",
  defaultExpanded = false,
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(content, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {}
  };

  const formatJson = (obj) => {
    if (!obj || typeof obj !== "object") {
      return JSON.stringify(obj, null, 2);
    }
    return JSON.stringify(obj, null, 2);
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCopy();
          }}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              Copy
            </>
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-3">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto bg-white p-3 rounded border">
            {formatJson(content)}
          </pre>
        </div>
      )}
    </div>
  );
}
